"use client";

import Logo from "@/components/common/logo";
import Image from "next/image";
import Link from "next/link";
import * as images from "@/utils/image";
import { useState } from "react";
import {
  FiShield,
  FiLock,
  FiUsers,
  FiCheckCircle,
  FiArrowRight,
  FiStar,
  FiHeart,
  FiZap,
} from "react-icons/fi";

const features = [
  {
    icon: <FiShield size={28} />,
    title: "Hivora: Collective Wisdom",
    desc: "Harness community, collective wisdom, belonging, and trust to make relationship discovery authentic and meaningful.",
    color: "#c28840",
    bg: "rgba(194, 136, 64, 0.08)",
    border: "rgba(194, 136, 64, 0.2)",
  },
  {
    icon: <FiUsers size={28} />,
    title: "Circle: Trusted Guidance",
    desc: "Invite your trusted inner circle—friends, family, and mentors—who help guide and validate your relationship decisions.",
    color: "#3b4cca",
    bg: "rgba(59, 76, 202, 0.08)",
    border: "rgba(59, 76, 202, 0.2)",
  },
  {
    icon: <FiHeart size={28} />,
    title: "Village-Backed Matching",
    desc: "Your village vouches for you, ensuring every match request is genuine, peer-validated, and built on real trust.",
    color: "#ea253d",
    bg: "rgba(234, 37, 61, 0.08)",
    border: "rgba(234, 37, 61, 0.2)",
  },
  {
    icon: <FiLock size={28} />,
    title: "Tokenized Security",
    desc: "Tokenized authentication and automatic data encryption keep your profile safe during critical interactions.",
    color: "#059669",
    bg: "rgba(5, 150, 105, 0.08)",
    border: "rgba(5, 150, 105, 0.2)",
  },
];

const steps = [
  {
    number: "01",
    title: "Create Your Profile",
    desc: "Sign up and build an authentic profile that truly represents who you are.",
  },
  {
    number: "02",
    title: "Build Your Circle",
    desc: "Invite your village—trusted friends and family—to join your Circle and guide your search.",
  },
  {
    number: "03",
    title: "Get Matched",
    desc: "Receive curated matches supported by collective wisdom and peer validation.",
  },
  {
    number: "04",
    title: "Connect & Grow",
    desc: "Chat, meet, and build lasting relationships backed by community trust.",
  },
];

const testimonials = [
  {
    name: "Sarah M.",
    role: "Product Designer",
    text: "Finally a platform where I know every match is real. My village approved everything and I feel genuinely safe.",
    rating: 5,
  },
  {
    name: "James K.",
    role: "Software Engineer",
    text: "The Hivora Circle concept is brilliant. Knowing my community vetted my matches makes all the difference.",
    rating: 5,
  },
  {
    name: "Priya R.",
    role: "Marketing Lead",
    text: "I've tried many apps. Hivora Circle is the first one that actually feels trustworthy and meaningful.",
    rating: 5,
  },
];

const LandingPage = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  return (
    <div className="lp_root">
      {/* ── NAV ── */}
      <nav className="lp_nav">
        <div className="lp_nav_inner">
          <Logo />
          <div className="lp_nav_links">
            {/* <Link href="/how-it-works" className="lp_nav_link">
              How It Works
            </Link> */}
            <Link href="/login" className="lp_nav_link">
              Sign In
            </Link>
            <Link href="/signup" className="lp_btn_primary lp_btn_sm">
              Get Started <FiArrowRight size={16} />
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lp_hero">
        <div className="lp_hero_glow lp_glow_1" />
        <div className="lp_hero_glow lp_glow_2" />

        <div className="lp_hero_inner">
          <div className="lp_hero_content">
            <div className="lp_badge">
              <FiShield size={14} />
              <span>Connection Guided by Community</span>
            </div>

            <h1 className="lp_hero_title">
              Where Your Village
              <br />
              <span className="lp_gradient_text">Helps Find Your Person</span>
            </h1>

            <p className="lp_hero_sub">
              The power of the hive applied to human connection—where your village,
              collective wisdom, and trusted circle help guide your relationship decisions.
            </p>

            <div className="lp_hero_actions">
              <Link href="/signup" className="lp_btn_primary lp_btn_lg">
                Start for Free <FiArrowRight size={18} />
              </Link>
              <Link href="#how-it-works" className="lp_btn_ghost lp_btn_lg">
                How It Works
              </Link>
            </div>
          </div>

          <div className="lp_hero_visual">
            <div className="lp_hero_img_wrap">
              <Image
                src={images.landing}
                alt="Hivora Circle App"
                width={560}
                height={580}
                className="lp_hero_img"
                priority
              />
              {/* Floating badges */}
              <div className="lp_float_card lp_float_card_1">
                <FiHeart size={16} style={{ color: "#ea253d" }} />
                <span>New Match!</span>
              </div>
              <div className="lp_float_card lp_float_card_2">
                <FiCheckCircle size={16} style={{ color: "#059669" }} />
                <span>Circle Approved</span>
              </div>
              <div className="lp_float_card lp_float_card_3">
                <FiStar size={16} style={{ color: "#c28840" }} />
                <span>Verified Profile</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="lp_section lp_features_section">
        <div className="lp_container">
          <div className="lp_section_head">
            <div className="lp_badge lp_badge_blue">
              <FiZap size={14} />
              <span>Brand Philosophy</span>
            </div>
            <h2 className="lp_section_title">
              Connection Guided by Community
            </h2>
            <p className="lp_section_sub">
              The power of the hive applied to human connection—where your village helps find your person.
            </p>
          </div>

          <div className="lp_features_grid">
            {features.map((f, i) => (
              <div
                key={i}
                className="lp_feature_card"
                style={{
                  "--feat-color": f.color,
                  "--feat-bg": f.bg,
                  "--feat-border": f.border,
                }}
              >
                <div className="lp_feat_icon_wrap">
                  <span style={{ color: f.color }}>{f.icon}</span>
                </div>
                <h3 className="lp_feat_title">{f.title}</h3>
                <p className="lp_feat_desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp_section lp_steps_section" id="how-it-works">
        <div className="lp_container">
          <div className="lp_section_head">
            <div className="lp_badge">
              <FiUsers size={14} />
              <span>Simple Process</span>
            </div>
            <h2 className="lp_section_title">How It Works</h2>
            <p className="lp_section_sub">
              Get started in minutes. Build your circle, get validated, and
              start meeting people who matter.
            </p>
          </div>

          <div className="lp_steps_grid">
            {steps.map((s, i) => (
              <div key={i} className="lp_step_card">
                <div className="lp_step_number">{s.number}</div>
                {i < steps.length - 1 && <div className="lp_step_connector" />}
                <h3 className="lp_step_title">{s.title}</h3>
                <p className="lp_step_desc">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="lp_steps_cta">
            <Link href="/signup" className="lp_btn_primary lp_btn_lg">
              Start Your Journey <FiArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      {/* <section className="lp_section lp_testimonials_section">
        <div className="lp_container">
          <div className="lp_section_head">
            <div className="lp_badge lp_badge_pink">
              <FiHeart size={14} />
              <span>Real Stories</span>
            </div>
            <h2 className="lp_section_title">Trusted by Thousands</h2>
          </div>

          <div className="lp_testimonials_wrap">
            <div className="lp_testimonial_card">
              <div className="lp_testi_stars">
                {Array.from({ length: testimonials[activeTestimonial].rating }).map((_, i) => (
                  <FiStar key={i} size={18} fill="#c28840" color="#c28840" />
                ))}
              </div>
              <p className="lp_testi_text">
                &ldquo;{testimonials[activeTestimonial].text}&rdquo;
              </p>
              <div className="lp_testi_author">
                <div className="lp_testi_avatar">
                  {testimonials[activeTestimonial].name[0]}
                </div>
                <div>
                  <div className="lp_testi_name">
                    {testimonials[activeTestimonial].name}
                  </div>
                  <div className="lp_testi_role">
                    {testimonials[activeTestimonial].role}
                  </div>
                </div>
              </div>
            </div>

            <div className="lp_testi_dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`lp_testi_dot ${i === activeTestimonial ? "lp_testi_dot_active" : ""}`}
                  onClick={() => setActiveTestimonial(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* ── CTA ── */}
      {/* <section className="lp_cta_section">
        <div className="lp_cta_glow" />
        <div className="lp_container lp_cta_inner">
          <h2 className="lp_cta_title">
            Ready to Trust Your Circle?
          </h2>
          <p className="lp_cta_sub">
            Join thousands of verified members finding meaningful connections
            backed by real trust.
          </p>
          <div className="lp_hero_actions">
            <Link href="/signup" className="lp_btn_white lp_btn_lg">
              Create Free Account <FiArrowRight size={18} />
            </Link>
            <Link href="/login" className="lp_btn_ghost_white lp_btn_lg">
              Sign In
            </Link>
          </div>
        </div>
      </section> */}

      {/* ── FOOTER ── */}
      <footer className="lp_footer">
        <div className="lp_footer_inner">
          <Logo />
          <div className="lp_footer_links">
            {/* <Link href="/how-it-works" className="lp_footer_link">
              How It Works
            </Link> */}
            <Link href="/privacy-policy" className="lp_footer_link">
              Privacy Policy
            </Link>
            <Link href="/terms-condition" className="lp_footer_link">
              Terms of Service
            </Link>
          </div>
          <p className="lp_footer_copy">
            © {new Date().getFullYear()} Hivora Circle. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
